import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, Lock, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function AdminLogin() {
  const { user, checking, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="w-6 h-6 animate-spin text-[#3A5A40]" />
      </div>
    );
  }
  if (user) return <Navigate to="/admin/editor" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate("/admin/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid credentials";
      toast.error(typeof msg === "string" ? msg : "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-12 text-center">
          <p className="overline text-[#9F5C45] mb-4">Supervisor sign in</p>
          <h1 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl tracking-tight">
            Welcome back.
          </h1>
          <p className="text-[#57534E] mt-4">Sign in to manage your clinic’s page.</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#E2DCD0] p-10 space-y-7" data-testid="admin-login-form">
          <label className="block">
            <span className="overline text-[#57534E] mb-2 block">Email</span>
            <input
              data-testid="admin-login-email"
              type="email"
              className="lgw-field"
              placeholder="letgowellness1@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </label>
          <label className="block">
            <span className="overline text-[#57534E] mb-2 block">Password</span>
            <input
              data-testid="admin-login-password"
              type="password"
              className="lgw-field"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 btn-primary text-sm tracking-wide disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" /> Sign in <ArrowUpRight className="w-4 h-4" />
              </>
            )}
          </button>

          <div className="flex items-center justify-between pt-1">
            <Link
              to="/admin/forgot"
              data-testid="admin-login-forgot-password"
              className="text-sm text-[#3A5A40] hover:text-[#1C1917] transition-colors"
            >
              Forgot password?
            </Link>
            <p className="text-xs text-[#57534E]">
              Secured by JWT · 7-day session
            </p>
          </div>
        </form>

        <p className="text-center mt-8">
          <a href="/" className="text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors">
            ← Back to public site
          </a>
        </p>
      </div>
    </main>
  );
}
